import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen, faXmark } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { useWeekMenu } from "../../WeekMenuContext"
import { ThemeDay } from "../../weekMenuTypes"
import { PlannerTag, PlannerTagVariant } from "../PlannerTag"
import { ThemeDishCard } from "./ThemeDishCard"

const useStyles = makeStyles()((theme) => ({
    panel: {
        position: "relative",
        zIndex: 3,
        minHeight: "100%",
        padding: ".75rem .75rem .875rem",
        background: theme.palette.tailwind.orange[50],
        borderRight: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    accentBar: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: ".25rem",
        background: theme.palette.tailwind.orange[500],
    },
    name: {
        flexGrow: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
        fontWeight: 700,
    },
    headerButton: {
        width: "1.375rem",
        height: "1.375rem",
        borderRadius: ".25rem",
        color: theme.palette.tailwind.slate[400],
        "& svg": {
            fontSize: ".75rem",
        },
    },
    addButton: {
        width: "100%",
        height: "auto",
        justifyContent: "center",
        padding: ".5rem .75rem",
    },
}))

type Props = Readonly<{
    dayIndex: number
    theme: ThemeDay
    onRename: () => void
    onRevert: () => void
}>

export function ThemeDayPanel({ dayIndex, theme: themeDay, onRename, onRevert }: Props) {
    const { classes } = useStyles()
    const { actions } = useWeekMenu()

    return (
        <Box className={classes.panel}>
            <Box className={classes.accentBar} />
            <Stack spacing="1.5rem">
                <Stack direction="row" alignItems="center" spacing=".25rem">
                    <Typography variant="body2" className={classes.name}>
                        {themeDay.name}
                    </Typography>
                    <LocalizedStrict id="merchant-week-menu-theme-panel-edit-tooltip" attrs={{ title: true }}>
                        <Tooltip arrow title="Edit theme name">
                            <IconButton size="small" className={classes.headerButton} onClick={onRename}>
                                <FontAwesomeIcon icon={faPen} />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-week-menu-theme-panel-revert-tooltip" attrs={{ title: true }}>
                        <Tooltip arrow title="Switch back to template">
                            <IconButton size="small" className={classes.headerButton} onClick={onRevert}>
                                <FontAwesomeIcon icon={faXmark} />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                </Stack>
                {themeDay.dishes.length > 0 && (
                    <Stack spacing=".75rem">
                        {themeDay.dishes.map((dish) => (
                            <ThemeDishCard
                                key={dish.id}
                                values={dish.values}
                                onChange={(values) => actions.updateThemeDish(dayIndex, dish.id, values)}
                                onRemove={() => actions.removeThemeDish(dayIndex, dish.id)}
                            />
                        ))}
                    </Stack>
                )}
                <PlannerTag
                    variant={PlannerTagVariant.ADD}
                    className={classes.addButton}
                    onClick={() => actions.addThemeDish(dayIndex)}
                >
                    <LocalizedStrict id="merchant-week-menu-theme-add-dish-button">
                        <span>Add dish</span>
                    </LocalizedStrict>
                </PlannerTag>
            </Stack>
        </Box>
    )
}
